import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, RefreshCw, XCircle } from "lucide-react";
import { api } from "convex/_generated/api";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";
import { springs } from "@/components/ui/motion";
import { StateCard } from "@/components/ui/StateCard";
import { Button } from "@/components/ui/primitives";
import { useApiMutation } from "@/packages/features-shared";
import { useAuditDetail } from "@/hooks/use-audit-detail";
import type { Id } from "@/packages/types";
import type { UIAuditCategory } from "@/packages/types";
import { useTranslation, getCurrentLanguage } from "@/packages/utils";

interface Step2GeneratingProps {
  auditId: Id<"audits">;
  auditName: string;
  onChecklistReady: (categories: UIAuditCategory[]) => void;
  onCancel?: () => void;
}

const GENERATION_TIMEOUT_MS = 180_000;

const SKELETON_ROWS = [
  { titleWidth: "60%", badgeWidth: "48px" },
  { titleWidth: "75%", badgeWidth: "56px" },
  { titleWidth: "50%", badgeWidth: "48px" },
  { titleWidth: "65%", badgeWidth: "40px" },
];

export function Step2Generating({
  auditId,
  auditName,
  onChecklistReady,
  onCancel,
}: Step2GeneratingProps) {
  const { t } = useTranslation("audits");
  const PROGRESS_MESSAGES = [
    t("wizard.generation.analyzingStandards"),
    t("wizard.generation.mappingRequirements"),
    t("wizard.generation.buildingItems"),
  ];

  const [messageIndex, setMessageIndex] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const readyFiredRef = useRef(false);

  const { categories } = useAuditDetail(auditId);

  const workspaceId = useWorkspaceId();

  // Observe checklist generation status from the backend
  const { data: generationStatus } = useWorkspaceQuery(
    api.audit_queries.getChecklistGenerationStatus,
    { auditId },
  );

  const retryMutation = useApiMutation(
    api.audit_mutations.retryChecklistGeneration,
    {
      name: "retry-checklist-generation",
      successMessage: t("messages.retryingGeneration"),
    },
  );

  const hasFailed = generationStatus?.status === "failed";

  // Rotate progress messages
  useEffect(() => {
    if (hasFailed) return;
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % PROGRESS_MESSAGES.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [hasFailed, PROGRESS_MESSAGES.length]);

  // Timeout after 120s
  useEffect(() => {
    if (hasFailed) return;
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, GENERATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [hasFailed]);

  // Auto-advance when checklist is ready
  useEffect(() => {
    if (categories && categories.length > 0 && !readyFiredRef.current) {
      readyFiredRef.current = true;
      onChecklistReady(categories);
    }
  }, [categories, onChecklistReady]);

  // Reset readyFiredRef when retrying (generation status goes back to "generating")
  useEffect(() => {
    if (generationStatus?.status === "generating") {
      readyFiredRef.current = false;
      setTimedOut(false);
    }
  }, [generationStatus?.status]);

  const handleRetry = () => {
    retryMutation.mutateAsync({
      auditId,
      workspaceId,
      locale: getCurrentLanguage(),
    });
  };

  // Error state: backend reported failure
  if (hasFailed) {
    return (
      <div className="flex flex-col items-center gap-6 py-8">
        <StateCard variant="empty">
          <StateCard.Icon />
          <StateCard.Title>{t("wizard.generation.failed")}</StateCard.Title>
          <StateCard.Description>
            {t("wizard.generation.error", { name: auditName })}
          </StateCard.Description>
        </StateCard>
        <div className="flex items-center gap-2 text-red-500">
          <XCircle className="h-4 w-4" />
          <span className="text-[12px] font-medium">
            {generationStatus.error || t("wizard.generation.generationFailed")}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            icon={RefreshCw}
            onClick={handleRetry}
            loading={retryMutation.isPending}
          >
            {t("wizard.generation.retryGeneration")}
          </Button>
          {onCancel && (
            <Button variant="secondary" onClick={onCancel}>
              {t("wizard.actions.cancel")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Timeout state: frontend timeout reached, but backend hasn't reported failure
  if (timedOut && (!categories || categories.length === 0)) {
    return (
      <div className="flex flex-col items-center gap-6 py-8">
        <StateCard variant="empty">
          <StateCard.Icon />
          <StateCard.Title>
            {t("wizard.generation.takingLonger")}
          </StateCard.Title>
          <StateCard.Description>
            The checklist generation for &ldquo;{auditName}&rdquo; hasn't
            completed yet. You can wait or try again.
          </StateCard.Description>
        </StateCard>
        <div className="flex items-center gap-2 text-amber-500">
          <AlertCircle className="h-4 w-4" />
          <span className="text-[12px] font-medium">
            {t("wizard.generation.stillProcessing")}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setTimedOut(false);
              readyFiredRef.current = false;
            }}
          >
            {t("wizard.generation.keepWaiting")}
          </Button>
          <Button
            variant="primary"
            icon={RefreshCw}
            onClick={handleRetry}
            loading={retryMutation.isPending}
          >
            {t("wizard.generation.retryGeneration")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-8">
      <StateCard variant="ai">
        <StateCard.Icon />
        <StateCard.Title>
          {t("wizard.generation.generatingChecklist")}
        </StateCard.Title>
        <StateCard.Description>
          {t("wizard.generation.analyzingAgainst", { name: auditName })}
        </StateCard.Description>
      </StateCard>

      <div className="w-full space-y-1">
        <AnimatePresence mode="wait">
          <motion.span
            key={messageIndex}
            className="block text-center text-[11px] font-medium text-accent"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            {PROGRESS_MESSAGES[messageIndex]}
          </motion.span>
        </AnimatePresence>

        <div className="mx-auto mt-1 h-[2px] w-48 rounded-full bg-border/50">
          <motion.div
            className="h-full rounded-full bg-accent"
            initial={{ width: "0%" }}
            animate={{ width: "90%" }}
            transition={{ duration: 8, ease: "easeOut" }}
          />
        </div>
      </div>

      <div className="w-full space-y-2">
        {SKELETON_ROWS.map((row, rowIndex) => (
          <motion.div
            key={`skeleton-${row.titleWidth}`}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              ...springs.gentle,
              delay: 0.3 + rowIndex * 0.15,
            }}
            className="flex items-center gap-3 rounded-lg bg-card/50 px-3 py-2.5 animate-pulse"
            style={{ animationDelay: `${rowIndex * 200}ms` }}
          >
            <div className="h-2 w-2 shrink-0 rounded-full bg-border" />
            <div
              className="h-3 rounded bg-border"
              style={{ width: row.titleWidth }}
            />
            <div className="ml-auto">
              <div
                className="h-4 rounded-full bg-border"
                style={{ width: row.badgeWidth }}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
